import * as qs from 'qs';
// tslint:disable-next-line no-import-side-effect
import 'whatwg-fetch';
import {
  USERNAME_VALIDATION_ENDPOINT,
  GET_PRICE_ENDPOINT,
  GET_DOMAIN_PRICE_ENDPOINT,
  VALIDATE_DOMAIN_ENDPOINT,
  READY_CHECK_INTERVAL,
  WAIT_FOR_WEBSITE_READY,
  CREATE_ENDPOINT,
  READY_ENDPOINT,
  GENERATE_CHECKOUT_CONFIG_ENDPOINT,
} from './constants';
import { langToLocale } from './helpers';
import delay from './delay';

const DEFAULT_PRICING_PERIOD = 1;

export const ERR_INVALID_TOKEN = 'invalid token';
export const ERR_CANNOT_VERIFY_TOKEN = 'cannot verify token';
export const ERR_FAILED_TO_RETRIEVE_LOGIN_LINK =
  'failed to retrieve login link';
export const ERR_DOMAIN_AVAILABILITY_FAILED =
  'domain availability check failed';

export const DomainCheckReponseCodes = {
  SUCCESS: 'SUCCESS',
  DOMAIN_IS_TRANSFERABLE: 'DOMAIN_IS_TRANSFERABLE',
  DOMAIN_IS_KNOWN_TO_US: 'DOMAIN_IS_KNOWN_TO_US',
  DOMAIN_IS_INVALID: 'DOMAIN_IS_INVALID',
  DOMAIN_IS_BLACKLISTED: 'DOMAIN_IS_BLACKLISTED',
  DOMAIN_CHECK_FAILED: 'DOMAIN_CHECK_FAILED',
  DOMAIN_INVALID_AS_PRIMARY_DOMAIN: 'DOMAIN_INVALID_AS_PRIMARY_DOMAIN',
  TLD_AVAILABLE_AT_PARTNER: 'TLD_AVAILABLE_AT_PARTNER',
  TLD_NOT_AVAILABLE: 'TLD_NOT_AVAILABLE',
  UNKNOWN: 'UNKNOWN',
};

interface ValidateSignupDataResponse {
  status: string;
  errors: {
    name?: string;
    password?: string;
    email?: string;
    terms?: string;
  };
}

class LoginLinkError extends Error {
  loginLink: string;

  constructor(loginLink: string) {
    super();
    this.loginLink = loginLink;
  }
}

// tslint:disable max-line-length
/**
 * @param payload Object - Object containing the value(s) to be evaluated
 *     the Object keys must be the name of the value that needs to be checked.
 *     e.g. if you want to evaluate an email the key for that value must be `email`
 *     all allowed keys can be found https://github.com/Jimdo/jimdo/blob/stable/webserver/mvc/modules/signup/controllers/IndexController.php#L262
 * @param lang string
 * @returns {Promise}
 */
// tslint:enable max-line-length
export const validateSignupData = async (
  payload: Object,
  lang: string
): Promise<ValidateSignupDataResponse> => {
  const queryData: Object = {
    locale: langToLocale(lang),
  };
  const query = qs.stringify(Object.assign({}, payload, queryData));

  const response = await fetch(`${USERNAME_VALIDATION_ENDPOINT}?${query}`);
  return response.json();
};

type PriceOptions = {
  priceType: string;
  period?: number;
  additionalProducts?: number[];
};

type DomainPriceOptions = {
  domainGroupType: string;
};

interface PriceResponse {
  total: string;
  currency: string;
}

interface DomainPriceResponse {
  price: string;
  currency: string;
}

export const getPrice = async (options: PriceOptions): Promise<string> => {
  const priceType = options.priceType;
  const additionalProducts = options.additionalProducts;
  const period = options.period || DEFAULT_PRICING_PERIOD;

  let additionalParameters = '';
  if (additionalProducts != null && additionalProducts.length > 0) {
    additionalParameters = `/additionalProducts/${additionalProducts.join(
      ','
    )}`;
  }

  const response = await fetch(
    `${GET_PRICE_ENDPOINT}/${priceType}/per/${period}${additionalParameters}`
  );
  const json: PriceResponse = await response.json();
  return json.total;
};

export const getDomainPrice = async (
  options: DomainPriceOptions
): Promise<string> => {
  const domainGroupType = options.domainGroupType;

  const response = await fetch(
    `${GET_DOMAIN_PRICE_ENDPOINT}${domainGroupType}`
  );
  const json: DomainPriceResponse = await response.json();

  return json.price;
};

interface ValidateDomainResponse {
  status: string;
  domain: string;
  domainFree?: boolean;
  error?: string;
}

export const validateDomain = async (
  domain: string,
  lang: string
): Promise<ValidateDomainResponse> => {
  const query = qs.stringify({
    domain,
    special: 'direct',
    package: 'pro',
  });

  const response = await fetch(`${VALIDATE_DOMAIN_ENDPOINT}/${lang}/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: query,
  });
  return await response.json();
};

/*
 * params to validate for
 * data that contains params as keys with their respective value
 */
interface LoginUrlUsernameResponse {
  payload: {
    ready: boolean;
    url?: string;
  };
}

export const getLoginUrlForUsername = async (
  username: string,
  lang: string,
  timeout: number = WAIT_FOR_WEBSITE_READY
): Promise<string> => {
  const locale: string = langToLocale(lang);
  const queryData: Object = {
    locale,
    username,
  };
  const query: string = qs.stringify(queryData);

  const start: number = Date.now();

  const checkReady = async (): Promise<string> => {
    const response = await fetch(`${READY_ENDPOINT}?${query}`);
    const json = (await response.json()) as LoginUrlUsernameResponse;
    const {
      payload: { ready, url: loginUrl },
    } = json;

    if (!ready) {
      const timeNow: number = Date.now();
      // we are checking for too long already
      if (timeNow - start > timeout) {
        throw new LoginLinkError(ERR_FAILED_TO_RETRIEVE_LOGIN_LINK);
      }
      await delay(READY_CHECK_INTERVAL);
      return checkReady();
    }

    return loginUrl;
  };

  try {
    return checkReady();
  } catch (e) {
    throw new LoginLinkError((e as Error).message);
  }
};

const fetchCreateWebsite = async (
  name: string,
  subtype: string,
  idToken: string,
  locale: string,
  refId?: string,
  trial?: string,
  trs?: string
  // tslint:disable-next-line no-any
): Promise<any> => {
  const data = {
    name,
    subtype,
    idToken,
    locale,
    refId,
    trial,
    easyMarketingTrackId: trs,
  };
  const query: string = qs.stringify(data);

  return fetch(CREATE_ENDPOINT, {
    method: 'POST',
    body: query,
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  });
};

/*
 * params to validate for
 * data that contains params as keys with their respective value
 */
interface CreateWebsiteResponse {
  status: string;
  errors?: Object;
}

/*
 * params to create website with
 * data that contains params as keys with their respective value
 */
export const createWebsite = async (
  name: string,
  subtype: string,
  lang: string,
  userIdToken: string,
  refId?: string,
  trial?: string,
  trs?: string
): Promise<CreateWebsiteResponse> => {
  const locale: string = langToLocale(lang);

  const response = await fetchCreateWebsite(
    name,
    subtype,
    userIdToken,
    locale,
    refId,
    trial,
    trs
  );

  const json = (await response.json()) as CreateWebsiteResponse;

  return json;
};

export interface CheckoutConfigBaseOptions {
  domain?: string;
  domainFree?: boolean;
  obfuscatedSubtype?: string;
  additionalProducts?: string;
  bundle?: string;
  package: string; // tslint:disable-line no-reserved-keywords
}

interface CheckoutConfigOptions extends CheckoutConfigBaseOptions {
  orderWithoutDomain: boolean;
}

const commonCheckoutDefaults = {
  websiteId: 0,
};

interface CheckoutConfigPayload extends CheckoutConfigOptions {
  websiteId: number;
  ref?: string;
  idToken?: string;
}

interface CheckoutConfigResponse {
  status: 'success' | 'error';
  message?: string;
  errors?: {
    checkout: string;
  };
}

export const generateCheckoutConfig = async (
  options: CheckoutConfigOptions,
  lang: string,
  userIdToken: string
): Promise<CheckoutConfigResponse> => {
  const payload: CheckoutConfigPayload = Object.assign(
    { idToken: userIdToken },
    commonCheckoutDefaults,
    options
  );

  const query = qs.stringify(payload);
  const response = await fetch(
    `${GENERATE_CHECKOUT_CONFIG_ENDPOINT}/${lang}/`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: query,
    }
  );
  const responseText = await response.json();

  if (response.status === 500) {
    return {
      status: 'error',
      errors: {
        checkout: responseText,
      },
    };
  }

  return {
    status: 'success',
    message: responseText,
  };
};

const checkoutDefaultsWithDomain = {
  orderWithoutDomain: false,
};

export const generateCheckoutConfigWithDomain = (
  options: CheckoutConfigBaseOptions,
  lang: string,
  userIdToken: string
): Promise<CheckoutConfigResponse> => {
  return generateCheckoutConfig(
    Object.assign({}, checkoutDefaultsWithDomain, options),
    lang,
    userIdToken
  );
};

const checkoutDefaultsWithoutDomain = {
  orderWithoutDomain: true,
};

export const generateCheckoutConfigWithoutDomain = (
  options: CheckoutConfigBaseOptions,
  lang: string,
  userIdToken: string
): Promise<CheckoutConfigResponse> => {
  return generateCheckoutConfig(
    Object.assign({}, checkoutDefaultsWithoutDomain, options),
    lang,
    userIdToken
  );
};
